export interface GitHubRepo {
  id: number;
  name: string;
  full_name: string;
  description: string | null;
  html_url: string;
  homepage: string | null;
  language: string | null;
  topics: string[];
  private: boolean;
  created_at: string;
  updated_at: string;
}

export interface GitHubReadme {
  content: string;
  encoding: string;
}

// Projeleri gizlemek için filtre listesi
const HIDDEN_REPOS = [
  'loadingcrm',
  'crmw_from',
  'biletodev3',
  'dersodev4',
  'dersodev3',
  'dersodevreact',
  'odev',
  'dersodev',
  'Furkan1287.github.io',
  'dnm',
  'odev3',
  'furkan1287'
];

export const fetchGitHubRepos = async (username: string): Promise<GitHubRepo[]> => {
  try {
    const response = await fetch(`https://api.github.com/users/${username}/repos?sort=updated&per_page=100`);
    if (!response.ok) {
      throw new Error('Failed to fetch repositories');
    }
    const repos: GitHubRepo[] = await response.json();
    // Sadece public repo'ları al ve gizli repo'ları filtrele
    return repos.filter(repo => 
      !repo.private && 
      !HIDDEN_REPOS.includes(repo.name)
    );
  } catch (error) {
    console.error('Error fetching GitHub repos:', error);
    return [];
  }
};

export const fetchReadme = async (owner: string, repo: string, isPrivate: boolean = false): Promise<string | null> => {
  try {
    // Önce GitHub API'yi dene (hem public hem private için çalışabilir)
    try {
      const response = await fetch(`https://api.github.com/repos/${owner}/${repo}/readme`, {
        headers: {
          'Accept': 'application/vnd.github.v3+json'
        }
      });
      if (response.ok) {
        const data: GitHubReadme = await response.json();
        // Decode base64 content with proper UTF-8 handling for Turkish characters
        try {
          const binaryString = atob(data.content);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const decoder = new TextDecoder('utf-8');
          const content = decoder.decode(bytes);
          return content;
        } catch (e) {
          // Fallback to simple atob if UTF-8 decoding fails
          try {
            return decodeURIComponent(escape(atob(data.content)));
          } catch (e2) {
            return atob(data.content);
          }
        }
      }
    } catch (apiError) {
      console.log('API fetch failed, trying raw content...');
    }

    // API başarısız olursa raw.githubusercontent.com'dan dene
    const branches = ['main', 'master', 'develop'];
    for (const branch of branches) {
      try {
        const response = await fetch(`https://raw.githubusercontent.com/${owner}/${repo}/${branch}/README.md`);
        if (response.ok) {
          try {
            const arrayBuffer = await response.arrayBuffer();
            const decoder = new TextDecoder('utf-8');
            const text = decoder.decode(arrayBuffer);
            if (text && !text.includes('404: Not Found')) {
              return text;
            }
          } catch (e) {
            // Fallback to text() if arrayBuffer fails
            const text = await response.text();
            if (text && !text.includes('404: Not Found')) {
              return text;
            }
          }
        }
      } catch (e) {
        continue;
      }
    }

    // README.md yerine readme.md (küçük harf) dene
    for (const branch of branches) {
      try {
        const response = await fetch(`https://raw.githubusercontent.com/${owner}/${repo}/${branch}/readme.md`);
        if (response.ok) {
          const text = await response.text();
          if (text && !text.includes('404: Not Found')) {
            return text;
          }
        }
      } catch (e) {
        continue;
      }
    }

    return null;
  } catch (error) {
    console.error('Error fetching README:', error);
    return null;
  }
};

export const getTechStack = (repo: GitHubRepo): string[] => {
  const tech: string[] = [];
  if (repo.language) tech.push(repo.language);
  if (repo.topics && repo.topics.length > 0) {
    tech.push(...repo.topics.slice(0, 5));
  }
  return tech;
};
